import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { PlusIcon, MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { generateQuoteNumber } from '../utils/quoteUtils';
import PillSelect from '../components/PillSelect';

const STATUS_FILTER_OPTIONS = [
  { value: '',          label: 'All statuses' },
  { value: 'draft',     label: 'Draft' },
  { value: 'sent',      label: 'Sent' },
  { value: 'accepted',  label: 'Accepted' },
  { value: 'declined',  label: 'Declined' },
  { value: 'expired',   label: 'Expired' },
  { value: 'cancelled', label: 'Cancelled' },
];

const ROW_STATUS_OPTIONS = STATUS_FILTER_OPTIONS.filter((o) => o.value);

const STATUS_PILL = {
  draft:     'bg-slate-100 text-slate-700 border-slate-300',
  sent:      'bg-sky-50 text-sky-700 border-sky-300',
  accepted:  'bg-emerald-50 text-emerald-700 border-emerald-300',
  declined:  'bg-rose-50 text-rose-700 border-rose-300',
  expired:   'bg-amber-50 text-amber-700 border-amber-300',
  cancelled: 'bg-slate-200 text-slate-700 border-slate-300',
};

const fmtDate = (d) => (d ? new Date(d + 'T00:00:00').toLocaleDateString() : '-');

export default function CustomerQuotes() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => { document.title = 'Customer Quotes'; }, []);

  const fetchQuotes = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('customer_quotes')
      .select(`
        id, quote_number, status, quote_date, quote_valid_days, drawing_number,
        revision, quoted_by, sent_at, accepted_at,
        customers(id, name, nickname)
      `)
      .order('quote_date', { ascending: false })
      .order('quote_number', { ascending: false });
    if (error) {
      console.error(error);
      toast.error('Failed to load quotes.');
    } else {
      setQuotes(data || []);
    }
    setLoading(false);
  };

  useEffect(() => { if (user) fetchQuotes(); }, [user]);

  const today = new Date().toISOString().slice(0, 10);

  const filtered = useMemo(() => {
    return quotes.filter((q) => {
      if (statusFilter && q.status !== statusFilter) return false;
      if (!search.trim()) return true;
      const t = search.toLowerCase();
      return (
        (q.quote_number || '').toLowerCase().includes(t) ||
        (q.drawing_number || '').toLowerCase().includes(t) ||
        (q.customers?.name || '').toLowerCase().includes(t) ||
        (q.customers?.nickname || '').toLowerCase().includes(t)
      );
    });
  }, [quotes, search, statusFilter]);

  const handleCreate = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const suggested = await generateQuoteNumber();
      const { data, error } = await supabase
        .from('customer_quotes')
        .insert({
          quote_number: suggested,
          status: 'draft',
          quoted_by: user?.email?.split('@')[0] || null,
          created_by: user?.id || null,
        })
        .select('id')
        .single();
      if (error) throw error;
      navigate(`/customer-quotes/${data.id}`);
    } catch (err) {
      console.error('Create quote failed:', err);
      toast.error(err.message || 'Failed to create quote.');
    } finally {
      setCreating(false);
    }
  };

  const handleRowStatusChange = async (quote, newStatus) => {
    if (newStatus === quote.status) return;
    try {
      const update = { status: newStatus };
      if (newStatus === 'sent' && !quote.sent_at) update.sent_at = new Date().toISOString();
      if (newStatus === 'accepted' && !quote.accepted_at) update.accepted_at = new Date().toISOString();
      if (newStatus === 'declined') update.declined_at = new Date().toISOString();
      const { error } = await supabase.from('customer_quotes').update(update).eq('id', quote.id);
      if (error) throw error;
      setQuotes((list) => list.map((q) => (q.id === quote.id ? { ...q, ...update } : q)));
      toast.success(`${quote.quote_number} → ${newStatus}`);
    } catch (err) {
      console.error('Status update failed:', err);
      toast.error(err.message || 'Failed to update status.');
      await fetchQuotes();
    }
  };

  const computeValidUntil = (q) => {
    if (!q.quote_date) return null;
    const d = new Date(q.quote_date + 'T00:00:00');
    d.setDate(d.getDate() + (q.quote_valid_days || 30));
    return d;
  };

  const isExpired = (q) => {
    if (q.status === 'expired' || q.status === 'declined' || q.status === 'cancelled' || q.status === 'accepted') return false;
    const vu = computeValidUntil(q);
    return vu && vu < new Date(today + 'T00:00:00');
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-semibold text-slate-800">Customer Quotes</h1>
        <button
          onClick={handleCreate}
          disabled={creating}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-slate-700 text-white rounded-sm hover:bg-slate-800 disabled:bg-slate-400"
        >
          <PlusIcon className="w-4 h-4" />
          {creating ? 'Creating…' : 'New Quote'}
        </button>
      </div>

      <div className="flex items-center gap-3 mb-3">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by quote #, drawing #, customer…"
            className="w-full pl-7 pr-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
        >
          {STATUS_FILTER_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wide">Quote #</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wide">Customer</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wide">Drawing #</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wide">Quote Date</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wide">Valid Until</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wide">Status</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wide">Quoted By</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-sm text-slate-400">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-10 text-center text-sm text-slate-400">
                  {quotes.length === 0 ? (
                    <div className="space-y-2">
                      <p>No customer quotes yet.</p>
                      <button
                        onClick={handleCreate}
                        disabled={creating}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-slate-700 text-white rounded-sm hover:bg-slate-800 disabled:bg-slate-400"
                      >
                        <PlusIcon className="w-4 h-4" /> Create your first quote
                      </button>
                    </div>
                  ) : 'No quotes match your filters.'}
                </td>
              </tr>
            ) : (
              filtered.map((q) => {
                const vu = computeValidUntil(q);
                const expired = isExpired(q);
                return (
                  <tr key={q.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 text-sm">
                      <Link to={`/customer-quotes/${q.id}`} className="text-sky-700 hover:underline font-medium">
                        {q.quote_number}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-700">
                      {q.customers?.nickname || q.customers?.name || '-'}
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-600">
                      {q.drawing_number || '-'}
                      {q.revision ? <span className="text-slate-400"> · rev {q.revision}</span> : null}
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-600">{fmtDate(q.quote_date)}</td>
                    <td className="px-3 py-2 text-sm">
                      {vu ? (
                        <span className={expired ? 'text-rose-700' : 'text-slate-600'}>
                          {vu.toLocaleDateString()}{expired ? ' (expired)' : ''}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <PillSelect
                        value={q.status || 'draft'}
                        onChange={(e) => handleRowStatusChange(q, e.target.value)}
                        options={ROW_STATUS_OPTIONS}
                        pillClass={STATUS_PILL[q.status] || 'bg-slate-100 text-slate-700 border-slate-300'}
                      />
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-600">{q.quoted_by || '-'}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
